import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  useChannels,
  useMessages,
  useSendMessage,
  useEditMessage,
  useDeleteMessage,
  useMarkRead,
  useMuteChannel,
  useCreateDM,
} from '../../api/hooks/messaging.hook';
import type { ChannelWithMeta, MessageInfo } from '../../api/services/messaging';
import { listOrgMembersForSharing } from '../../api/services/sharing';
import type { OrgMemberForSharing } from '../../api/services/sharing';

// ── New Message Dialog ───────────────────────────────────────────────────

const NewMessageDialog = ({
  isOpen,
  onClose,
  onCreateChannel,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreateChannel: (channelId: string) => void;
}) => {
  const [members, setMembers] = useState<OrgMemberForSharing[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const createDM = useCreateDM();

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      listOrgMembersForSharing()
        .then((data) => {
          setMembers(data.members);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load org members', err);
          setLoading(false);
        });
    }
  }, [isOpen]);

  const filteredMembers = members.filter((m) =>
    m.email.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = async (userId: string) => {
    try {
      const channel = await createDM.mutateAsync({ user_id: userId });
      onCreateChannel(channel.channel.id);
      onClose();
      setSearch('');
    } catch (err) {
      console.error('Failed to create DM channel', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">New Message</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-neutral-200">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email..."
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="text-sm text-neutral-400 text-center py-8">
              Loading members...
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-sm text-neutral-400 text-center py-8">
              {search ? 'No members found' : 'No members available'}
            </div>
          ) : (
            filteredMembers.map((member) => (
              <button
                key={member.user_id}
                type="button"
                onClick={() => handleSelect(member.user_id)}
                disabled={createDM.isLoading}
                className="w-full text-left px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {member.email}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ── Channel List ─────────────────────────────────────────────────────

const ChannelList = ({
  channels,
  selectedId,
  onSelect,
  onNewMessage,
}: {
  channels: ChannelWithMeta[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNewMessage: () => void;
}) => (
  <div className="w-64 shrink-0 border-r border-neutral-200 bg-white flex flex-col overflow-hidden">
    <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-neutral-900">Channels</h2>
      <button
        type="button"
        onClick={onNewMessage}
        className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
      >
        + New
      </button>
    </div>
    <nav className="flex-1 overflow-y-auto py-1">
      {channels.map((item) => {
        const ch = item.channel;
        const isActive = ch.id === selectedId;
        const label =
          ch.type === 'project'
            ? ch.name || 'Project Channel'
            : 'Direct Message';
        return (
          <button
            type="button"
            key={ch.id}
            onClick={() => onSelect(ch.id)}
            className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
              isActive
                ? 'bg-blue-50 text-blue-700 font-medium'
                : 'text-neutral-600 hover:bg-neutral-50'
            }`}
          >
            <span className="truncate">
              {ch.type === 'direct' ? '@ ' : '# '}
              {label}
            </span>
            {item.unread_count > 0 && (
              <span className="ml-1.5 text-[10px] font-bold bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                {item.unread_count > 99 ? '99+' : item.unread_count}
              </span>
            )}
          </button>
        );
      })}
      {channels.length === 0 && (
        <div className="px-4 py-8 text-center">
          <p className="text-xs text-neutral-400 mb-3">No channels yet.</p>
          <button
            type="button"
            onClick={onNewMessage}
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Start a conversation
          </button>
        </div>
      )}
    </nav>
  </div>
);

// ── Message Bubble ───────────────────────────────────────────────────

const MessageBubble = ({
  msg,
  isOwn,
  onEdit,
  onDelete,
}: {
  msg: MessageInfo;
  isOwn: boolean;
  onEdit: (id: string, content: string) => void;
  onDelete: (id: string) => void;
}) => {
  const time = new Date(msg.created_at).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className={`group flex flex-col mb-2 ${isOwn ? 'items-end' : 'items-start'}`}
    >
      <div
        className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
          msg.content_type === 'system'
            ? 'bg-neutral-100 text-neutral-500 italic text-xs text-center w-full max-w-full'
            : isOwn
              ? 'bg-blue-600 text-white'
              : 'bg-neutral-100 text-neutral-900'
        }`}
      >
        {msg.reply_to && (
          <div className="text-[10px] opacity-60 mb-1">Reply</div>
        )}
        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
      </div>
      <div className="flex items-center gap-2 mt-0.5 px-1">
        <span className="text-[10px] text-neutral-400">{time}</span>
        {msg.edited_at && (
          <span className="text-[10px] text-neutral-400">(edited)</span>
        )}
        {isOwn && msg.content_type !== 'system' && (
          <span className="hidden group-hover:flex gap-1">
            <button
              type="button"
              onClick={() => onEdit(msg.id, msg.content)}
              className="text-[10px] text-neutral-400 hover:text-blue-500"
            >
              edit
            </button>
            <button
              type="button"
              onClick={() => onDelete(msg.id)}
              className="text-[10px] text-neutral-400 hover:text-red-500"
            >
              delete
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

// ── Compose Bar ──────────────────────────────────────────────────────

const ComposeBar = ({
  onSend,
  isLoading,
}: {
  onSend: (content: string) => void;
  isLoading: boolean;
}) => {
  const [text, setText] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex gap-2 px-4 py-3 border-t border-neutral-200 bg-white"
    >
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type a message..."
        className="flex-1 border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
        disabled={isLoading}
      />
      <button
        type="submit"
        disabled={isLoading || !text.trim()}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        Send
      </button>
    </form>
  );
};

// ── Channel Header ───────────────────────────────────────────────────

const ChannelHeader = ({
  channel,
  muted,
  onMute,
}: {
  channel: ChannelWithMeta;
  muted: boolean;
  onMute: () => void;
}) => {
  const ch = channel.channel;
  const label =
    ch.type === 'project'
      ? ch.name || 'Project Channel'
      : 'Direct Message';
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white">
      <div>
        <h2 className="text-sm font-semibold text-neutral-900">
          {ch.type === 'direct' ? '@ ' : '# '}
          {label}
        </h2>
        <span className="text-[10px] text-neutral-400">
          {ch.type === 'project' ? 'Project channel' : 'Direct message'}
        </span>
      </div>
      <button
        type="button"
        onClick={onMute}
        className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded border border-neutral-200"
      >
        {muted ? 'Unmute' : 'Mute'}
      </button>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────

const MessagesPage = () => {
  const { data: channelData, isLoading: channelsLoading } = useChannels();
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const {
    data: messageData,
    isLoading: messagesLoading,
  } = useMessages(selectedChannelId);
  const sendMsg = useSendMessage();
  const editMsg = useEditMessage();
  const deleteMsg = useDeleteMessage();
  const markRead = useMarkRead();
  const muteCh = useMuteChannel();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const channels = channelData?.channels ?? [];
  const messages = messageData?.messages ?? [];
  const selectedChannel = channels.find(
    (c) => c.channel.id === selectedChannelId,
  );

  // Auto-select first channel
  useEffect(() => {
    if (!selectedChannelId && channels.length > 0) {
      setSelectedChannelId(channels[0].channel.id);
    }
  }, [channels, selectedChannelId]);

  // Mark as read when selecting a channel
  useEffect(() => {
    if (selectedChannelId) {
      markRead.mutate(selectedChannelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (content: string) => {
    if (!selectedChannelId) return;
    sendMsg.mutate({ channelId: selectedChannelId, content });
  };

  const handleEdit = (messageId: string, currentContent: string) => {
    const newContent = prompt('Edit message:', currentContent);
    if (newContent !== null && newContent.trim() && selectedChannelId) {
      editMsg.mutate({
        channelId: selectedChannelId,
        messageId,
        content: newContent.trim(),
      });
    }
  };

  const handleDelete = (messageId: string) => {
    if (!selectedChannelId) return;
    if (confirm('Delete this message?')) {
      deleteMsg.mutate({ channelId: selectedChannelId, messageId });
    }
  };

  const handleMute = () => {
    if (!selectedChannelId || !selectedChannel) return;
    muteCh.mutate({
      channelId: selectedChannelId,
      muted: !selectedChannel.muted,
    });
  };

  const handleNewChannelCreated = (channelId: string) => {
    setSelectedChannelId(channelId);
  };

  // TODO: get current userId from auth context
  const currentUserId = '';

  if (channelsLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-neutral-400">
        Loading channels...
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full overflow-hidden bg-neutral-50">
        <ChannelList
          channels={channels}
          selectedId={selectedChannelId}
          onSelect={setSelectedChannelId}
          onNewMessage={() => setShowNewMessageDialog(true)}
        />

      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedChannel ? (
          <>
            <ChannelHeader
              channel={selectedChannel}
              muted={selectedChannel.muted}
              onMute={handleMute}
            />

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {messagesLoading ? (
                <div className="text-sm text-neutral-400 text-center py-8">
                  Loading messages...
                </div>
              ) : messages.length > 0 ? (
                messages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    msg={msg}
                    isOwn={msg.sender_id === currentUserId}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))
              ) : (
                <div className="text-sm text-neutral-400 text-center py-8">
                  No messages yet. Start the conversation!
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <ComposeBar
              onSend={handleSend}
              isLoading={sendMsg.isLoading}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-neutral-400">
            Select a channel to start messaging.
          </div>
        )}
      </div>
    </div>
    <NewMessageDialog
        isOpen={showNewMessageDialog}
        onClose={() => setShowNewMessageDialog(false)}
        onCreateChannel={handleNewChannelCreated}
      />
    </>
  );
};

export default MessagesPage;
